import { useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { useNavigate, Link } from 'react-router-dom';

import { 
  goalLabels,
  physicalActivityLabels,
  weightLabel,
} from '../../constants/questioner';
import { excludeMealLabels } from '../../constants/excludeMeals';
import { setStepsData } from '../../actions/steps';
import { logout, deleteAccount } from '../../actions/auth';
import { showPopup } from '../../actions/popup';
import cn from 'classnames';

import styles from './Sidebar.module.css';

const NOT_SET = 'Not set';

const sidebarItems = [
  {
    title: 'Biological sex',
    step: 1,
    value: stepsData => {
      if (stepsData.gender) {
        return stepsData.gender === 'man' ? 'Male' : 'Female';
      }
      return NOT_SET;
    },
  },
  {
    title: 'Date of Birth',
    step: 2,
    value: stepsData => {
      if (stepsData.birth_date) {
        return moment(stepsData.birth_date).format('D MMM YYYY');
      }
      return NOT_SET;
    },
  },
  {
    title: 'Weight',
    step: 3,
    value: stepsData => {
      if (stepsData.weight) {
        return `${stepsData.weight} ${weightLabel[stepsData.weight_unit]}`;
      }
      return NOT_SET;
    },
  },
  {
    title: 'Height',
    step: 4,
    value: stepsData => {
      if (stepsData.height) {
        return `${stepsData.height} ${stepsData.height_unit}`;
      }
      return NOT_SET;
    },
  },
  {
    title: 'Fitness level',
    step: 5,
    value: stepsData => {
      if (stepsData.physical_activity) {
        return physicalActivityLabels[stepsData.physical_activity];
      }
      return NOT_SET;
    },
  },
  {
    title: 'Selected proteins',
    step: 6,
    value: stepsData => {
      if (stepsData.excluded_meals?.length) {
        const excludedMeals = stepsData.excluded_meals.map(item => excludeMealLabels[item]);
        return excludedMeals.join(', ');
      }
      return NOT_SET;
    },
  },
  {
    title: 'Selected allergies',
    step: 7,
    value: stepsData => {
      if (stepsData.allergies?.length) {
        const allergies = stepsData.allergies.map(item => item.name);
        return allergies.join(', ');
      }
      return NOT_SET;
    },
  },
  {
    title: 'Foods I Avoid',
    step: 8,
    value: stepsData => {
      if (stepsData.disliked_products?.length) {
        const products = stepsData.disliked_products.map(item => item.name);
        return products.join(', ');
      }
      return NOT_SET;
    },
  },
  {
    title: 'Goal',
    step: 9,
    value: stepsData => {
      if (stepsData.goal) {
        return goalLabels[stepsData.goal];
      }
      return NOT_SET;
    },
  },
  {
    title: 'Meal plan days',
    step: 10,
    value: stepsData => {
      if (stepsData.days) {
        return stepsData.days;
      }
      return NOT_SET;
    },
  },
  // {
  //   title: 'Recipe level',
  //   onClick: onEdit,
  //   value: stepsData => {
  //     if (stepsData.birthDate) {
  //       return;
  //     }
  //     return NOT_SET;
  //   },
  // },
  {
    title: 'Cooking prep',
    step: 13,
    value: stepsData => {
      if (typeof stepsData.preparations_in_advance === 'number') {
        return stepsData.preparations_in_advance ? 'Yes, I do' : 'No, I don’t';
      }
      return NOT_SET;
    },
  },
  {
    title: 'Chronic diseases',
    step: 11,
    value: stepsData => {
      if (stepsData.chronic_diseases) {
        return stepsData.chronic_diseases;
      }
      return NOT_SET;
    },
  },
  {
    title: 'Meals per day',
    step: 14,
    value: stepsData => {
      if (typeof stepsData.meals_per_day === 'number') {
        return stepsData.meals_per_day;
      }
      return NOT_SET;
    },
  },
  {
    title: 'Alcoholic/fizzy drinks',
    step: 15,
    value: stepsData => {
      if (typeof stepsData.sweet_drinks === 'number') {
        return stepsData.sweet_drinks;
      }
      return NOT_SET;
    },
  },
];

const Sidebar = (props) => {
  const { 
    onHide, 
    logout,
    deleteAccount,
    showPopup,
    stepsData,
  } = props;

  const [showEditData, setShowEditData] = useState(false);
  // const [showEditData, setShowEditData] = useState(true);
  const navigate = useNavigate();

  console.log('===stepsData', stepsData);

  const onDeleteAccountHandler = () => {
    showPopup('Are your sure you want to delete your data?', deleteAccount);
  };

  const onGoToEditData = (step) => {
    stepsData.step = step;
    setStepsData(stepsData);
    navigate('/edit');
  };

  const renderContent = () => {
    if (showEditData) {
      return (
        <>
          <div className={styles.sidebarTitle}>
            <div 
              className={styles.backButton}
              onClick={() => setShowEditData(false)}
            />
            Edit data
          </div>
          {sidebarItems.map((item, index) => (
            <div 
              key={index}
              className={styles.editButton}
              onClick={() => onGoToEditData(item.step)}
            >
              <div> 
                {item.title}
              </div>
              <div>
                {item.value(stepsData)}
              </div>
            </div>
          ))}
        </>
      );
    }

    return (
      <>
        <div className={styles.sidebarTitle}>
          Account
        </div>
        <div className={styles.buttonGroup}>
          <div 
            className={cn(styles.sidebarButton, styles.editData)}
            onClick={() => setShowEditData(true)}
          >
            Edit data
          </div>
        </div>
        <div className={styles.buttonGroup}>
          <div className={styles.groupTitle}>
            Privacy
          </div>
          <a 
            className={cn(styles.sidebarButton, styles.privacy)} 
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy
          </a>
          <a 
            className={cn(styles.sidebarButton, styles.terms)} 
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms & Conditions
          </a>
        </div>
        <div className={styles.buttonGroup}>
          <div className={styles.groupTitle}>
            Help
          </div>
          <Link 
            className={cn(styles.sidebarButton, styles.contact)} 
            to="/contact"
          >
            Contact support
          </Link>
        </div>
        <div className={styles.buttonGroup}>
          <div 
            className={cn(styles.sidebarButton, styles.delete, styles.redButton)}
            onClick={onDeleteAccountHandler}
          >
            Delete account & data
          </div>
          <div 
            className={cn(styles.sidebarButton, styles.logout, styles.redButton)}
            onClick={logout}
          >
            Log out
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={styles.sidebarWrapper} onClick={onHide}>
      <div 
        className={styles.sidebar}
        onClick={(event) => event.stopPropagation()} 
      >
        <div 
          className={styles.cross} 
          onClick={onHide} 
        />
        <div className={styles.sidebarContent}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userData: state.auth.userData,
  stepsData: state.steps.stepsData,
});

const mapDispatchToProps = {
  logout,
  deleteAccount,
  showPopup,
};

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
